// Phase 10 — PhasePane (DECISIONS §E.1–E.4, README §6.7).
//
// The reusable single-phase cooking surface shared by LiveCook and by
// each side of the split Prep screen. Top to bottom:
//   - optional phase timer (unused now that every timed screen shows the
//     elapsed/total timer in its container top bar);
//   - OutstandingPanel — only when the phase has timed tasks;
//   - Timeline — the phase progress indicator (phase-chip row variant);
//   - body row: look-back rail + task column
//       · revealed completed tasks (start-time order, slider-revealed)
//       · active task panels — every task in its active window, each a
//         countdown dial (multi-active);
//       · upcoming tasks (start-time order).
//
// Overrun tasks live ONLY in the OutstandingPanel (not inline), unifying
// the prep and live treatments. Completed tasks leave the column via the
// slide-out animation and are reachable through the look-back slider.
//
// Time labels on the active cards are phase-relative (e.g. 00:00:00 →
// 00:00:15), derived from the earliest task start in the pane — the A0
// Start cue sits at phase t=0, so that is the phase origin.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AssistEntry, ClassifiedPlan, ScheduleEntry } from '@cookchow/shared';
import {
  ActiveTaskCard,
  AssistPopover,
  LookbackSlider,
  NotePopover,
  OutstandingPanel,
  TaskRectangle,
  Timeline,
  TimerDisplay,
  type OutstandingItem,
  type TaskRectangleState,
  type TimelineMode,
} from '../design/components';
import type { AssistMatcher } from './assist';
import { useCountdown } from './useCountdown';
import { useNow } from './useNow';
import { useSlideOutOnComplete } from './useSlideOutOnComplete';
import { notesForTask, type NoteEntry } from './notes';
import { buildOutstandingItems, phaseHasTimedTasks } from './phaseLayout';
import { formatHHMM, formatHHMMSS } from './helpers';

function visualForState(state: 'upcoming' | 'active' | 'overrun' | 'done'): TaskRectangleState {
  if (state === 'done') return 'completed';
  if (state === 'active') return 'active';
  if (state === 'overrun') return 'incompletePast';
  return 'upcoming';
}

// One active task panel + its own countdown clock. A child component so
// `useCountdown` is called once per active task without breaking the
// rules of hooks when the active set grows or shrinks.
function ActiveTaskDial({
  task,
  phaseStartMs,
  clockOffset,
  handedness,
  livecookExpired,
  hasNote,
  assistMatcher,
  onAssistWord,
  onChefHat,
  onCheck,
}: {
  task: ScheduleEntry;
  phaseStartMs: number;
  clockOffset: number;
  handedness: 'left' | 'right';
  livecookExpired: boolean;
  hasNote: boolean;
  assistMatcher?: AssistMatcher;
  onAssistWord?: (word: string) => void;
  onChefHat?: () => void;
  onCheck: (id: string) => void;
}) {
  const { durationSeconds, remainingSeconds } = useCountdown(
    task.startAt,
    task.mustCompleteBy,
    clockOffset,
  );
  const startLabel = formatHHMMSS((Date.parse(task.startAt) - phaseStartMs) / 1000);
  const endLabel = formatHHMMSS((Date.parse(task.mustCompleteBy) - phaseStartMs) / 1000);
  return (
    <ActiveTaskCard
      title={task.action}
      startLabel={startLabel}
      endLabel={endLabel}
      time={formatHHMM(task.startAt)}
      endTime={formatHHMM(task.mustCompleteBy)}
      durationSeconds={durationSeconds}
      remainingSeconds={remainingSeconds}
      lane={task.lane}
      chefHatRef={hasNote}
      checkboxSide={handedness}
      livecookExpired={livecookExpired}
      startAtIso={task.startAt}
      assistMatcher={assistMatcher}
      onAssistWord={onAssistWord}
      onCheck={() => onCheck(task.id)}
      onChefHatClick={onChefHat}
    />
  );
}

// The dormant "next" card, shown only during an early-finish gap (no task
// active). It elevates the next task with a greyed checkbox and a FULL,
// static ring (the clock has not started). When the task's authored time
// arrives the classifier flips it to `active` and the pane renders it via
// ActiveTaskDial instead — the ring then starts emptying and the box
// un-greys. No `useCountdown` here: the ring is static until that handover.
function PendingNextDial({
  task,
  phaseStartMs,
  handedness,
  hasNote,
  assistMatcher,
  onAssistWord,
  onChefHat,
}: {
  task: ScheduleEntry;
  phaseStartMs: number;
  handedness: 'left' | 'right';
  hasNote: boolean;
  assistMatcher?: AssistMatcher;
  onAssistWord?: (word: string) => void;
  onChefHat?: () => void;
}) {
  const durationSeconds = Math.max(
    0,
    Math.round((Date.parse(task.mustCompleteBy) - Date.parse(task.startAt)) / 1000),
  );
  const startLabel = formatHHMMSS((Date.parse(task.startAt) - phaseStartMs) / 1000);
  const endLabel = formatHHMMSS((Date.parse(task.mustCompleteBy) - phaseStartMs) / 1000);
  return (
    <ActiveTaskCard
      variant="pending"
      title={task.action}
      startLabel={startLabel}
      endLabel={endLabel}
      time={formatHHMM(task.startAt)}
      endTime={formatHHMM(task.mustCompleteBy)}
      durationSeconds={durationSeconds}
      remainingSeconds={durationSeconds}
      lane={task.lane}
      chefHatRef={hasNote}
      checkboxSide={handedness}
      startAtIso={task.startAt}
      assistMatcher={assistMatcher}
      onAssistWord={onAssistWord}
      onChefHatClick={onChefHat}
    />
  );
}

export interface PhasePaneProps {
  /** Tasks belonging to this pane's phase(s). */
  tasks: readonly ScheduleEntry[];
  classified: ClassifiedPlan | null;
  clockOffset: number;
  handedness: 'left' | 'right';
  timelineMode: TimelineMode;
  /** Optional per-pane timer (Prep panes). LiveCook omits it. */
  timer?: { seconds: number; label: string; expired?: boolean };
  /** Damped-red overlay for LiveCook past nominal duration. */
  livecookExpired?: boolean;
  /** ARIA label for the pane region. */
  ariaLabel: string;
  /** refId → glossary entry, for resolving each task's chef-hat notes.
   *  Absent ⇒ no chef-hat affordance is shown on this pane. */
  noteLookup?: Map<string, NoteEntry>;
  onCheck: (id: string) => void;
  /** Renders extra content after a task in the active/upcoming column
   *  (LiveCook uses this to slot the Cooking-complete indicator). */
  renderAfterTask?: (taskId: string) => ReactNode;
  /** When set, replaces the whole task area (look-back rail + task column)
   *  with this node, centred. LiveCook uses it to show the end-of-cook
   *  "Cooking complete!" button alone once the timer has finished. The
   *  OutstandingPanel, timeline and timer above are unaffected. */
  taskAreaOverride?: ReactNode;
  /** Kitchen Assist matcher. When present, help words in active and
   *  forthcoming task titles become links (README §12.2). Completed
   *  look-back cards and the outstanding panel are deliberately excluded. */
  assistMatcher?: AssistMatcher;
  /** Resolve a canonical help word to its entry for the popover body. */
  resolveAssistEntry?: (word: string) => Promise<AssistEntry | null>;
  /** Phase chip label for the timeline row (PreCook/PrepCook/LiveCook). When
   *  set, the timeline renders the per-phase ROW variant (chip + moving
   *  now-chip, no static start label) instead of the start/finish band. */
  timelinePhaseChip?: string;
}

export function PhasePane({
  tasks,
  classified,
  clockOffset,
  handedness,
  timelineMode,
  timer,
  livecookExpired = false,
  ariaLabel,
  noteLookup,
  onCheck,
  renderAfterTask,
  taskAreaOverride,
  assistMatcher,
  resolveAssistEntry,
  timelinePhaseChip,
}: PhasePaneProps) {
  const ids = useMemo(() => new Set(tasks.map((t) => t.id)), [tasks]);

  // Which task's glossary popover is open (one at a time), and a resolver
  // from a task to its skill/note entries.
  const [openNoteId, setOpenNoteId] = useState<string | null>(null);
  const notesFor = (t: ScheduleEntry): NoteEntry[] =>
    noteLookup ? notesForTask(t, noteLookup) : [];
  const toggleNote = (id: string): void => setOpenNoteId((cur) => (cur === id ? null : id));

  // Which task's assist help popover is open (one at a time), with the
  // matched word and its resolved entry (null while loading). Anchored under
  // the card whose word was clicked.
  const [assistState, setAssistState] = useState<{
    taskId: string;
    word: string;
    entry: AssistEntry | null;
  } | null>(null);

  const handleAssistWord = (taskId: string, word: string): void => {
    // Opening assist closes any open glossary popover and vice-versa.
    setOpenNoteId(null);
    setAssistState({ taskId, word, entry: null });
    void resolveAssistEntry?.(word)
      .then((entry) => {
        setAssistState((cur) =>
          cur && cur.taskId === taskId && cur.word === word ? { ...cur, entry } : cur,
        );
      })
      .catch(() => {
        setAssistState((cur) => (cur && cur.taskId === taskId && cur.word === word ? null : cur));
      });
  };

  // Only wire assist when a matcher and resolver are both present.
  const assistEnabled = !!assistMatcher && !!resolveAssistEntry;

  const stateById = new Map<string, 'upcoming' | 'active' | 'overrun' | 'done'>();
  if (classified) {
    for (const ct of classified.tasks) {
      if (ids.has(ct.task.id)) stateById.set(ct.task.id, ct.state);
    }
  }

  const phaseStartMs = useMemo(() => {
    let min = Number.POSITIVE_INFINITY;
    for (const t of tasks) {
      const ms = Date.parse(t.startAt);
      if (Number.isFinite(ms) && ms < min) min = ms;
    }
    return Number.isFinite(min) ? min : 0;
  }, [tasks]);

  // Expected finish = the latest task deadline in the pane. For LiveCook
  // this equals the immutable serve time by construction (the last task's
  // mustCompleteBy is phase.start + nominalDuration; see projection.ts);
  // for a prep pane it is that phase's end.
  const phaseFinishMs = useMemo(() => {
    let max = Number.NEGATIVE_INFINITY;
    for (const t of tasks) {
      const ms = Date.parse(t.mustCompleteBy);
      if (Number.isFinite(ms) && ms > max) max = ms;
    }
    return Number.isFinite(max) ? max : 0;
  }, [tasks]);

  // Present time, re-sampled each second so the timeline chip advances
  // between server ticks.
  const nowMs = useNow(clockOffset);
  const progress =
    phaseFinishMs > phaseStartMs ? (nowMs - phaseStartMs) / (phaseFinishMs - phaseStartMs) : 0;

  // Active set — every task currently in its window (multi-active),
  // ordered by start time for a stable layout.
  const activeTasks = useMemo(
    () =>
      tasks
        .filter((t) => (stateById.get(t.id) ?? 'upcoming') === 'active')
        .slice()
        .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks, classified],
  );

  // Completed tasks, start-time order, for the look-back slider.
  const completedPast = useMemo(
    () =>
      tasks
        .filter((t) => (stateById.get(t.id) ?? 'upcoming') === 'done')
        .slice()
        .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks, classified],
  );

  const completedIdsForAnim = useMemo(() => completedPast.map((t) => t.id), [completedPast]);
  const { slidingOut } = useSlideOutOnComplete(completedIdsForAnim);

  // Upcoming column — upcoming tasks, plus just-completed ones still
  // sliding out. Overrun tasks are NOT here; they live in the
  // OutstandingPanel.
  const upcoming = useMemo(
    () =>
      tasks.filter((t) => {
        const v = stateById.get(t.id) ?? 'upcoming';
        if (v === 'done') return slidingOut.has(t.id);
        return v === 'upcoming';
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks, classified, slidingOut],
  );

  // Dormant "next" card during an early-finish gap (README discussion):
  // when a task has been completed and nothing is currently active, elevate
  // the earliest not-yet-started task as a greyed, static preview until its
  // time arrives. Not shown for the initial idle before the first task
  // (gap-after-completion only), nor when something is already active.
  const nextTask = useMemo(() => {
    const candidates = upcoming
      .filter((t) => (stateById.get(t.id) ?? 'upcoming') === 'upcoming')
      .slice()
      .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt));
    return candidates[0] ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcoming, classified]);

  const hasCompleted = tasks.some((t) => (stateById.get(t.id) ?? 'upcoming') === 'done');
  const showDormantNext = activeTasks.length === 0 && nextTask !== null && hasCompleted;

  // "Worked-through": nothing left actively in play in this pane — no
  // active task, no dormant next, and no upcoming rows (everything is done
  // or parked in the outstanding panel). Drives the small phase-complete
  // button. There must be at least one task in the pane to count.
  // Outstanding tabs — overrun tasks, ordered by end time. The display
  // data (label, times) is sourced from the immutable `schedule` prop —
  // the same source the active/upcoming/completed rows use — so the panel
  // never depends on the shape of `classified.tasks[].task`. `classified`
  // only decides *which* ids are overrun. Times + overdue recompute each
  // second via nowMs.
  const outstandingItems: OutstandingItem[] = useMemo(
    () => buildOutstandingItems(tasks, classified, nowMs),
    [classified, tasks, nowMs],
  );

  // Look-back slider.
  const [lookback, setLookback] = useState(0);
  useEffect(() => {
    if (lookback > completedPast.length) setLookback(completedPast.length);
  }, [completedPast.length, lookback]);
  const revealedPast =
    lookback > 0 ? completedPast.slice(Math.max(0, completedPast.length - lookback)) : [];

  const rail = (
    <aside
      aria-label="Look-back side rail"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: '0.25rem',
      }}
    >
      <LookbackSlider
        value={lookback}
        max={completedPast.length}
        onChange={setLookback}
        orientation="vertical"
      />
    </aside>
  );

  return (
    // role="group" (not a landmark region) so the OutstandingPanel below
    // remains the single region within the pane.
    <div
      role="group"
      aria-label={ariaLabel}
      style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1, minWidth: 0 }}
    >
      {timer && (
        <TimerDisplay seconds={timer.seconds} expired={timer.expired} label={timer.label} />
      )}

      {phaseHasTimedTasks(tasks) && <OutstandingPanel items={outstandingItems} onCheck={onCheck} />}

      <Timeline
        mode={timelineMode}
        phaseChip={timelinePhaseChip}
        startLabel={
          timelinePhaseChip ? undefined : formatHHMM(new Date(phaseStartMs).toISOString())
        }
        finishLabel={formatHHMM(new Date(phaseFinishMs).toISOString())}
        progress={progress}
        nowLabel={formatHHMM(new Date(nowMs).toISOString())}
      />

      {taskAreaOverride ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '160px',
            padding: '1rem 0',
          }}
        >
          {taskAreaOverride}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: handedness === 'right' ? 'row-reverse' : 'row',
            gap: '0.75rem',
            alignItems: 'flex-start',
          }}
        >
          {rail}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
            }}
          >
            {revealedPast.length > 0 && (
              <div
                role="group"
                aria-label="Recently completed tasks"
                style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
              >
                {revealedPast.map((t) => {
                  const entries = notesFor(t);
                  return (
                    <div key={`past-${t.id}`} style={{ position: 'relative' }}>
                      <TaskRectangle
                        state="completed"
                        overrunHistory={(stateById.get(t.id) ?? 'upcoming') === 'overrun'}
                        title={t.action}
                        time={formatHHMM(t.startAt)}
                        endTime={formatHHMM(t.mustCompleteBy)}
                        startAtIso={t.startAt}
                        lane={t.lane}
                        chefHatRef={entries.length > 0}
                        checkboxSide={handedness}
                        onCheck={() => onCheck(t.id)}
                        onChefHatClick={entries.length > 0 ? () => toggleNote(t.id) : undefined}
                      />
                      {openNoteId === t.id && entries.length > 0 && (
                        <NotePopover
                          title={t.action}
                          entries={entries}
                          onClose={() => setOpenNoteId(null)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {activeTasks.length > 0 && (
              <div
                role="group"
                aria-label="Active tasks"
                style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
              >
                {activeTasks.map((t) => {
                  const entries = notesFor(t);
                  return (
                    <div
                      key={t.id}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                      }}
                    >
                      <ActiveTaskDial
                        task={t}
                        phaseStartMs={phaseStartMs}
                        clockOffset={clockOffset}
                        handedness={handedness}
                        livecookExpired={livecookExpired}
                        hasNote={entries.length > 0}
                        assistMatcher={assistEnabled ? assistMatcher : undefined}
                        onAssistWord={
                          assistEnabled ? (word) => handleAssistWord(t.id, word) : undefined
                        }
                        onChefHat={entries.length > 0 ? () => toggleNote(t.id) : undefined}
                        onCheck={onCheck}
                      />
                      {openNoteId === t.id && entries.length > 0 && (
                        <NotePopover
                          title={t.action}
                          entries={entries}
                          onClose={() => setOpenNoteId(null)}
                        />
                      )}
                      {assistState?.taskId === t.id && (
                        <AssistPopover
                          word={assistState.word}
                          entry={assistState.entry}
                          onClose={() => setAssistState(null)}
                        />
                      )}
                      {renderAfterTask?.(t.id)}
                    </div>
                  );
                })}
              </div>
            )}

            {showDormantNext && nextTask && (
              <div
                role="group"
                aria-label="Next task"
                style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
              >
                {(() => {
                  const entries = notesFor(nextTask);
                  return (
                    <div
                      style={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                      }}
                    >
                      <PendingNextDial
                        task={nextTask}
                        phaseStartMs={phaseStartMs}
                        handedness={handedness}
                        hasNote={entries.length > 0}
                        assistMatcher={assistEnabled ? assistMatcher : undefined}
                        onAssistWord={
                          assistEnabled ? (word) => handleAssistWord(nextTask.id, word) : undefined
                        }
                        onChefHat={entries.length > 0 ? () => toggleNote(nextTask.id) : undefined}
                      />
                      {openNoteId === nextTask.id && entries.length > 0 && (
                        <NotePopover
                          title={nextTask.action}
                          entries={entries}
                          onClose={() => setOpenNoteId(null)}
                        />
                      )}
                      {assistState?.taskId === nextTask.id && (
                        <AssistPopover
                          word={assistState.word}
                          entry={assistState.entry}
                          onClose={() => setAssistState(null)}
                        />
                      )}
                      {renderAfterTask?.(nextTask.id)}
                    </div>
                  );
                })()}
              </div>
            )}

            <div
              role="group"
              aria-label="Upcoming tasks"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                // Deliberate breathing room between the active panel and the
                // remaining tasks (README §6.7.3); upcoming cards stay tight.
                marginTop: activeTasks.length > 0 ? '0.5rem' : undefined,
              }}
            >
              {upcoming
                .filter((t) => !(showDormantNext && t.id === nextTask?.id))
                .map((t) => {
                  const visual = visualForState(stateById.get(t.id) ?? 'upcoming');
                  const entries = notesFor(t);
                  return (
                    <div
                      key={t.id}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                      }}
                    >
                      <TaskRectangle
                        state={visual}
                        livecookExpired={livecookExpired && visual !== 'completed'}
                        title={t.action}
                        time={formatHHMM(t.startAt)}
                        endTime={formatHHMM(t.mustCompleteBy)}
                        startAtIso={t.startAt}
                        lane={t.lane}
                        chefHatRef={entries.length > 0}
                        checkboxSide={handedness}
                        // Active-only checkbox: a not-yet-started task shows no
                        // box (it can't be completed); it appears with the
                        // clock once the task goes active. Completed tasks
                        // sliding out keep their tick.
                        showCheckbox={visual !== 'upcoming'}
                        slidingOut={slidingOut.has(t.id)}
                        // Assist links on forthcoming tasks only — not on the
                        // just-completed cards sliding out of the column.
                        assistMatcher={
                          assistEnabled && visual !== 'completed' ? assistMatcher : undefined
                        }
                        onAssistWord={
                          assistEnabled && visual !== 'completed'
                            ? (word) => handleAssistWord(t.id, word)
                            : undefined
                        }
                        onCheck={() => onCheck(t.id)}
                        onChefHatClick={entries.length > 0 ? () => toggleNote(t.id) : undefined}
                      />
                      {openNoteId === t.id && entries.length > 0 && (
                        <NotePopover
                          title={t.action}
                          entries={entries}
                          onClose={() => setOpenNoteId(null)}
                        />
                      )}
                      {assistState?.taskId === t.id && (
                        <AssistPopover
                          word={assistState.word}
                          entry={assistState.entry}
                          onClose={() => setAssistState(null)}
                        />
                      )}
                      {renderAfterTask?.(t.id)}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PhasePane;
